"use client";

import { timeframeToSeconds } from "@opensea/graphql";
import { createContext, useContext, useMemo, useState } from "react";
import { useCurrencyPriceUpdateSubscription } from "@/hooks/api/use-currency-price-update-subscription";
import { useSenderQueryParam } from "@/hooks/query-param/use-sender-query-param";
import { useAddress } from "@/hooks/wallet";
import { useTokenSwapActivityAnnotations } from "../activity/TokenSwapActivityAnnotation/use-token-swap-activity-annotations";
const CurrencyChartContext = createContext({
  hoveredPrice: undefined,
  setHoveredPrice: () => undefined,
  selectedCandle: undefined,
  setSelectedCandle: () => undefined,
  chartStartingPrice: undefined,
  setChartStartingPrice: () => undefined,
  chartEndingPrice: undefined,
  setChartEndingPrice: () => undefined,
  liveCandles: {
    ONE_DAY: undefined,
    FOUR_HOURS: undefined,
    FIFTEEN_MINUTES: undefined
  },
  livePrice: undefined,
  annotations: []
});
export function CurrencyChartProvider({
  children,
  contractIdentifier,
  timeframe
}) {
  const [hoveredPrice, setHoveredPrice] = useState();
  const [selectedCandle, setSelectedCandle] = useState();
  const [chartStartingPrice, setChartStartingPrice] = useState();
  const [chartEndingPrice, setChartEndingPrice] = useState();
  const [liveCandles, setLiveCandles] = useState({});
  const [livePrice, setLivePrice] = useState();

  // Compute annotations (token swap activity) and share via context
  const {
    sender: senderQueryParam
  } = useSenderQueryParam();
  const address = useAddress();
  const filter = {
    sender: senderQueryParam || address
  };
  const {
    annotations
  } = useTokenSwapActivityAnnotations({
    contract: contractIdentifier,
    filter,
    pause: !(contractIdentifier && filter.sender),
    timeframe
  });
  const filteredAnnotations = useMemo(() => {
    const refTime = livePrice?.timestamp ? new Date(livePrice.timestamp) : new Date();
    const limit = timeframeToSeconds(timeframe);
    return (annotations || []).filter(a => {
      const diffSeconds = (refTime.getTime() - a.date.getTime()) / 1000;
      return diffSeconds >= 0 && diffSeconds < limit;
    });
  }, [annotations, livePrice?.timestamp, timeframe]);

  // Subscribe to high-frequency price-only updates to keep current price fresh
  useCurrencyPriceUpdateSubscription(contractIdentifier || null, update => {
    if (update.livePrice) {
      setLivePrice(update.livePrice);
    }
    setLiveCandles({
      ONE_DAY: update.oneDayCandle ?? undefined,
      FOUR_HOURS: update.fourHourCandle ?? undefined,
      FIFTEEN_MINUTES: update.fifteenMinuteCandle ?? undefined
    });
  });
  const value = {
    hoveredPrice,
    setHoveredPrice,
    selectedCandle,
    setSelectedCandle,
    chartStartingPrice,
    setChartStartingPrice,
    chartEndingPrice,
    setChartEndingPrice,
    liveCandles,
    livePrice,
    annotations: filteredAnnotations
  };
  return <CurrencyChartContext.Provider value={value}>
      {children}
    </CurrencyChartContext.Provider>;
}
export function useCurrencyChart() {
  return useContext(CurrencyChartContext);
}